import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Tv, CheckCircle, Loader2, ChevronLeft, Star, Calendar, Sparkles, ListVideo, ShieldAlert } from 'lucide-react';

const fmt = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

const STEPS = [
  { id: 1, label: 'Durée' },
  { id: 2, label: 'Écrans' },
  { id: 3, label: 'Contenu' },
  { id: 4, label: 'Résumé' },
];

export const PurchaseFlowModal = ({
  offers = [],
  contentLists = [],
  currentCredit = 0,
  isReferred = false,
  referralDiscount = 0,
  promoCode,
  setPromoCode,
  promoValidation,
  promoValidating,
  validatePromoCode,
  clearPromoCode,
  paymentLoading,
  paymentError,
  onClose,
  onPay,
}) => {
  const [step, setStep] = useState(1);

  const defaultDuration = useMemo(
    () => offers.find((o) => o.featured)?.key || offers[0]?.key || null,
    [offers]
  );
  const [selectedDuration, setSelectedDuration] = useState(defaultDuration);
  useEffect(() => {
    if (!selectedDuration && defaultDuration) setSelectedDuration(defaultDuration);
  }, [defaultDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contenu (liste/bouquet + adulte)
  const [selectedTemplateId, setSelectedTemplateId] = useState(contentLists[0]?.template_id ?? null);
  const [isAdult, setIsAdult] = useState(false);
  useEffect(() => {
    if (selectedTemplateId == null && contentLists.length) setSelectedTemplateId(contentLists[0].template_id);
  }, [contentLists]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedContent = contentLists.find((c) => c.template_id === selectedTemplateId) || contentLists[0] || null;
  useEffect(() => {
    if (selectedContent && !selectedContent.has_adult && isAdult) setIsAdult(false);
  }, [selectedTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentOffer = offers.find((o) => o.key === selectedDuration) || null;
  const availableScreens = currentOffer?.screens || [];

  // Renouvellement automatique : éligible uniquement 1 / 3 / 6 mois (règle backend)
  const autoRenewEligible = [1, 3, 6].includes(currentOffer?.months);
  const [autoRenew, setAutoRenew] = useState(false);
  useEffect(() => {
    if (!autoRenewEligible && autoRenew) setAutoRenew(false);
  }, [selectedDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedScreens, setSelectedScreens] = useState(availableScreens[0]?.screens || 1);
  useEffect(() => {
    if (!availableScreens.length) return;
    if (!availableScreens.some((s) => s.screens === selectedScreens)) {
      setSelectedScreens(availableScreens[0].screens);
    }
  }, [selectedDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  const screenEntry =
    availableScreens.find((s) => s.screens === selectedScreens) || availableScreens[0] || null;
  const basePrice = screenEntry?.price || 0;

  // Comparateur d'économies : mensuel équivalent vs durée active la plus courte (mêmes écrans)
  const savings = useMemo(() => {
    if (!screenEntry || !currentOffer) return null;
    const candidates = offers
      .map((o) => ({ months: o.months, label: o.label, s: o.screens.find((x) => x.screens === selectedScreens) }))
      .filter((c) => c.s && c.months);
    if (candidates.length < 2) return null;
    const shortest = candidates.reduce((a, b) => (a.months < b.months ? a : b));
    if (shortest.months >= currentOffer.months) return null;
    const refMonthly = shortest.s.price / shortest.months;
    const thisMonthly = basePrice / currentOffer.months;
    const pct = Math.round((1 - thisMonthly / refMonthly) * 100);
    const saved = refMonthly * currentOffer.months - basePrice;
    if (pct <= 0) return null;
    return { pct, saved, refLabel: shortest.label };
  }, [selectedDuration, selectedScreens, offers]); // eslint-disable-line react-hooks/exhaustive-deps

  const promoDiscount = promoValidation?.valid ? promoValidation.discount_euros : 0;
  // Remise de parrainage : uniquement sur les accès 1 an.
  const referralEligibleDuration = selectedDuration === '1year';
  const referralApplied = isReferred && referralDiscount > 0 && referralEligibleDuration
    ? Math.min(referralDiscount, basePrice) : 0;
  const priceAfterPromo = Math.max(0, basePrice - promoDiscount - referralApplied);
  const creditApplied = Math.min(currentCredit, priceAfterPromo);
  const finalPrice = Math.max(0, priceAfterPromo - creditApplied);

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] rounded-t-2xl sm:rounded-lg metallic-border w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="purchase-flow-modal"
      >
        {/* Header + step indicator */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center space-x-2">
            <CreditCard className="h-5 w-5 text-[#FF6B35]" />
            <span>Souscrire un abonnement</span>
          </h3>
          <button
            onClick={handleClose}
            className="text-[#6B6B6E] hover:text-white p-2"
            disabled={paymentLoading}
            data-testid="close-payment-modal"
          >
            &#10005;
          </button>
        </div>

        <div className="flex items-center justify-between mb-6" data-testid="purchase-step-indicator">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    step >= s.id ? 'bg-[#FF6B35] text-black' : 'bg-[#2A2A2D] text-[#6B6B6E]'
                  }`}
                >
                  {step > s.id ? <CheckCircle className="h-4 w-4" /> : s.id}
                </div>
                <span className={`text-xs font-semibold ${step >= s.id ? 'text-white' : 'text-[#6B6B6E]'}`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${step > s.id ? 'bg-[#FF6B35]' : 'bg-[#2A2A2D]'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ÉTAPE 1 — Durée */}
        {step === 1 && (
          <div className="space-y-3" data-testid="step-duration">
            <p className="text-[#B8B8BA] text-sm">Choisissez la durée de votre abonnement.</p>
            <div className="space-y-2">
              {offers.map((offer) => {
                const active = selectedDuration === offer.key;
                return (
                  <button
                    key={offer.key}
                    onClick={() => setSelectedDuration(offer.key)}
                    className={`w-full text-left p-4 rounded-lg border transition-all relative ${
                      active ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D] hover:border-[#B8B8BA]'
                    }`}
                    data-testid={`duration-option-${offer.key}`}
                  >
                    {(offer.badge || offer.featured) && (
                      <span className="absolute -top-2 right-3 bg-[#FF6B35] text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        {offer.featured && <Star className="h-3 w-3" />}
                        {offer.badge || 'À la une'}
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className={`h-5 w-5 ${active ? 'text-[#FF6B35]' : 'text-[#6B6B6E]'}`} />
                        <div>
                          <div className="text-white font-bold">{offer.label}</div>
                          <div className="text-[#6B6B6E] text-xs">
                            soit {fmt(offer.from_monthly)}&euro;/mois
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#6B6B6E] text-[11px]">à partir de</div>
                        <div className="text-[#FF6B35] font-bold text-lg">{fmt(offer.from_price)}&euro;</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setStep(2)}
              disabled={!selectedDuration}
              className="btn-primary w-full py-3 disabled:opacity-50"
              data-testid="duration-continue"
            >
              Continuer
            </button>
          </div>
        )}

        {/* ÉTAPE 2 — Écrans */}
        {step === 2 && (
          <div className="space-y-3" data-testid="step-screens">
            <p className="text-[#B8B8BA] text-sm">
              Combien d'écrans souhaitez-vous utiliser en simultané ? ({currentOffer?.label})
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {availableScreens.map((s) => {
                const active = selectedScreens === s.screens;
                return (
                  <button
                    key={s.screens}
                    onClick={() => setSelectedScreens(s.screens)}
                    className={`p-4 rounded-lg border text-center transition-all relative ${
                      active ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D] hover:border-[#B8B8BA]'
                    }`}
                    data-testid={`screen-option-${s.screens}`}
                  >
                    {s.featured && (
                      <span className="absolute -top-2 right-2 bg-[#FF6B35] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        {s.badge || '★'}
                      </span>
                    )}
                    <Tv className={`h-6 w-6 mx-auto mb-1 ${active ? 'text-[#FF6B35]' : 'text-[#6B6B6E]'}`} />
                    <div className="text-white text-lg font-bold">{s.screens}</div>
                    <div className="text-[#6B6B6E] text-[11px]">écran{s.screens > 1 ? 's' : ''}</div>
                    <div className="text-[#FF6B35] text-sm font-bold mt-1">{fmt(s.price)}&euro;</div>
                    <div className="text-[#6B6B6E] text-[10px]">{fmt(s.monthly_equivalent)}&euro;/mois</div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setStep(1)} className="btn-secondary flex-1 py-3 flex items-center justify-center gap-1" data-testid="screens-back">
                <ChevronLeft className="h-4 w-4" /> Retour
              </button>
              <button onClick={() => setStep(3)} disabled={!selectedScreens} className="btn-primary flex-1 py-3 disabled:opacity-50" data-testid="screens-continue">
                Continuer
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 3 — Contenu (liste/bouquet + adulte) */}
        {step === 3 && (
          <div className="space-y-3" data-testid="step-content">
            <p className="text-[#B8B8BA] text-sm flex items-center gap-2">
              <ListVideo className="h-4 w-4 text-[#FF6B35]" /> Choisissez votre liste de chaînes.
            </p>
            <div className="space-y-2">
              {contentLists.map((c) => {
                const active = selectedTemplateId === c.template_id;
                return (
                  <button
                    key={c.template_id}
                    onClick={() => setSelectedTemplateId(c.template_id)}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      active ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D] hover:border-[#B8B8BA]'
                    }`}
                    data-testid={`content-option-${c.template_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold">{c.name}</span>
                      {active && <CheckCircle className="h-5 w-5 text-[#FF6B35]" />}
                    </div>
                    {c.description && <p className="text-[#6B6B6E] text-xs mt-1">{c.description}</p>}
                  </button>
                );
              })}
              {contentLists.length === 0 && (
                <p className="text-[#6B6B6E] text-sm">Liste standard appliquée automatiquement.</p>
              )}
            </div>

            {/* Contenu adulte */}
            <button
              onClick={() => selectedContent?.has_adult && setIsAdult(!isAdult)}
              disabled={!selectedContent?.has_adult}
              className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                isAdult ? 'border-[#FF3B5C] bg-[#FF3B5C]/10' : 'border-[#3A3A3D]'
              } ${!selectedContent?.has_adult ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="adult-toggle"
            >
              <span className="flex items-center gap-2 text-sm text-white">
                <ShieldAlert className="h-4 w-4 text-[#FF3B5C]" /> Inclure le contenu adulte (+18)
              </span>
              <span className={`w-10 h-5 rounded-full relative transition-colors ${isAdult ? 'bg-[#FF3B5C]' : 'bg-[#3A3A3D]'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${isAdult ? 'left-5' : 'left-0.5'}`} />
              </span>
            </button>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setStep(2)} className="btn-secondary flex-1 py-3 flex items-center justify-center gap-1" data-testid="content-back">
                <ChevronLeft className="h-4 w-4" /> Retour
              </button>
              <button onClick={() => setStep(4)} className="btn-primary flex-1 py-3" data-testid="content-continue">
                Continuer
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 4 — Résumé */}
        {step === 4 && (
          <div className="space-y-4" data-testid="step-summary">
            <div className="bg-[#222226] p-4 rounded-lg border border-[#FF6B35]/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8BA] text-sm">Abonnement</span>
                <span className="text-white font-bold">
                  {currentOffer?.label} — {selectedScreens} écran{selectedScreens > 1 ? 's' : ''}
                </span>
              </div>
              {selectedContent && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#B8B8BA] text-sm">Contenu</span>
                  <span className="text-white text-sm font-semibold">
                    {selectedContent.name}{isAdult ? ' + adulte' : ''}
                  </span>
                </div>
              )}
              <div className="text-center py-2">
                <div className="text-3xl sm:text-4xl font-bold text-[#FF6B35]">{fmt(basePrice)}&euro;</div>
                <p className="text-[#6B6B6E] text-xs mt-1">+15 000 chaînes en 4K • {fmt(basePrice / (currentOffer?.months || 1))}&euro;/mois</p>
              </div>
              {savings && (
                <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-lg p-2.5 flex items-center gap-2" data-testid="savings-comparator">
                  <Sparkles className="h-4 w-4 text-[#00FF88]" />
                  <span className="text-[#00FF88] text-xs font-semibold">
                    Économisez {savings.pct}% vs {savings.refLabel} (soit -{fmt(savings.saved)}&euro;)
                  </span>
                </div>
              )}
            </div>

            {/* Code promo */}
            {!isReferred ? (
              <div className="bg-[#222226] p-3 sm:p-4 rounded-lg border border-[#3A3A3D]" data-testid="promo-code-section">
                <label className="text-[#B8B8BA] text-xs font-semibold uppercase tracking-wider mb-2 block">Code promo</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      if (promoValidation) clearPromoCode();
                    }}
                    placeholder="Entrez votre code"
                    className="flex-1 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none"
                    disabled={paymentLoading || promoValidation?.valid}
                    data-testid="promo-code-input"
                  />
                  {promoValidation?.valid ? (
                    <button onClick={clearPromoCode} className="bg-[#3A3A3D] text-white px-3 py-2 rounded text-sm hover:bg-[#4A4A4D] transition-colors" data-testid="promo-code-clear">
                      &#10005;
                    </button>
                  ) : (
                    <button onClick={validatePromoCode} disabled={!promoCode.trim() || promoValidating} className="bg-[#FF6B35] text-black px-4 py-2 rounded text-sm font-semibold hover:bg-[#F7931E] transition-colors disabled:opacity-50" data-testid="promo-code-apply">
                      {promoValidating ? '...' : 'Appliquer'}
                    </button>
                  )}
                </div>
                {promoValidation?.valid && (
                  <div className="mt-2 flex items-center gap-2 text-[#00FF88] text-xs">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span>-{promoValidation.discount_euros}&euro; appliqué ({promoValidation.influencer_pseudo})</span>
                  </div>
                )}
                {promoValidation && !promoValidation.valid && (
                  <p className="mt-2 text-[#FF3B5C] text-xs">{promoValidation.error}</p>
                )}
              </div>
            ) : (
              <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-3 rounded-lg" data-testid="promo-code-disabled">
                <p className="text-[#FFB800] text-xs">Vous bénéficiez d'un lien de parrainage. Les codes promo ne sont pas cumulables.</p>
              </div>
            )}

            {/* Récap prix */}
            <div className="bg-[#00FF88]/5 border border-[#3A3A3D] p-3 sm:p-4 rounded-lg" data-testid="price-breakdown">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#B8B8BA]">Prix de base :</span>
                <span className="text-white">{fmt(basePrice)}&euro;</span>
              </div>
              {promoDiscount > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#B8B8BA]">Réduction code promo :</span>
                  <span className="text-[#00FF88] font-bold">-{fmt(promoDiscount)}&euro;</span>
                </div>
              )}
              {referralApplied > 0 && (
                <div className="flex justify-between text-sm mb-2" data-testid="referral-discount-row">
                  <span className="text-[#B8B8BA]">Réduction parrainage :</span>
                  <span className="text-[#00FF88] font-bold">-{fmt(referralApplied)}&euro;</span>
                </div>
              )}
              {creditApplied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#B8B8BA]">Votre crédit :</span>
                  <span className="text-[#00FF88] font-bold">-{fmt(creditApplied)}&euro;</span>
                </div>
              )}
              <div className="flex justify-between text-base mt-2 pt-2 border-t border-[#3A3A3D]">
                <span className="text-white font-semibold">À payer :</span>
                <span className="text-[#FF6B35] font-bold text-lg sm:text-xl" data-testid="final-price">{fmt(finalPrice)}&euro;</span>
              </div>
            </div>

            {paymentError && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 p-3 rounded-lg">
                <p className="text-[#FF3B5C] text-sm">{paymentError}</p>
              </div>
            )}

            <div className="bg-[#222226] p-3 rounded-lg border border-[#3A3A3D]">
              <p className="text-[#B8B8BA] text-xs">Paiement sécurisé via Stripe. Abonnement activé immédiatement après confirmation.</p>
            </div>

            {autoRenewEligible && (
              <button
                type="button"
                onClick={() => setAutoRenew(!autoRenew)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${autoRenew ? 'border-[#00FF88] bg-[#00FF88]/10' : 'border-[#3A3A3D] bg-[#222226]'}`}
                data-testid="auto-renew-toggle"
              >
                <span className="text-left">
                  <span className="block text-white text-sm font-semibold">Renouvellement automatique</span>
                  <span className="block text-[#6B6B6E] text-[11px]">Votre abonnement sera renouvelé automatiquement au tarif en vigueur. Résiliable à tout moment.</span>
                </span>
                <span className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ml-3 ${autoRenew ? 'bg-[#00FF88]' : 'bg-[#3A3A3D]'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${autoRenew ? 'left-5' : 'left-0.5'}`} />
                </span>
              </button>
            )}

            <div className="flex gap-2">
              <button onClick={() => setStep(3)} disabled={paymentLoading} className="btn-secondary py-3 px-4 flex items-center justify-center gap-1" data-testid="summary-back">
                <ChevronLeft className="h-4 w-4" /> Retour
              </button>
              <button
                onClick={() => onPay(selectedDuration, selectedScreens, selectedTemplateId, isAdult, autoRenewEligible ? autoRenew : false)}
                disabled={paymentLoading}
                className="btn-primary flex-1 py-3 flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="pay-button"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Payer {fmt(finalPrice)}&euro;</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PurchaseFlowModal;
